import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Box, 
  Grid, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Checkbox, 
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Avatar,
  Chip,
  styled,
  LinearProgress,
  CardMedia
} from '@mui/material';
import { PhotoCamera, Delete, CheckCircle, LocalOffer, Star, Business, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, getDoc, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import StripePayment from '../components/Payments/StripePayment';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.1)',
  background: 'linear-gradient(to bottom, #ffffff, #f9f9f9)',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  padding: '12px 24px',
  fontWeight: '600',
  textTransform: 'none',
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[3],
  },
}));

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    fontWeight: '600',
    fontSize: '0.9rem',
    '&.Mui-active': {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e0e0e0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.light,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
  },
}));

const SellerTypeCard = styled(Card)(({ theme, selected }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  boxShadow: selected ? theme.shadows[4] : theme.shadows[1],
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
  },
}));

const PlanCard = styled(Card)(({ theme, selected, color }) => ({
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: selected ? `2px solid ${color}` : '1px solid #ddd',
  backgroundColor: selected ? `${color}10` : 'background.paper',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
  },
}));

const PostAd = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [adType, setAdType] = useState('');
  const [carType, setCarType] = useState('');
  const [images, setImages] = useState([]);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [sellerType, setSellerType] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [userAdCount, setUserAdCount] = useState(0);
  const [stepCompleted, setStepCompleted] = useState(false);
  const dealerLimitReached = sellerType === 'dealer' && !userData?.activePlan && userAdCount >= (userData?.subscription === 'basic' ? 100 : 10);

  // Common car details
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [fuelType, setFuelType] = useState('gasoline');
  const [transmission, setTransmission] = useState('automatic');
  const [enginePower, setEnginePower] = useState('');
  const [mileage, setMileage] = useState('');
  const [registered, setRegistered] = useState('yes');
  const [urgent, setUrgent] = useState(false);
  const [contactNumber, setContactNumber] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [viberNumber, setViberNumber] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const [carBodyType, setCarBodyType] = useState('');
  const [driveType, setDriveType] = useState('');
  const [city, setCity] = useState('');

  // Dealer specific fields
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [facebookLink, setFacebookLink] = useState('');
  const [instagramLink, setInstagramLink] = useState('');

  // Equipment
  const [equipment, setEquipment] = useState({
    esp: false,
    abs: false,
    navigation: false,
    electricSeats: false,
    climate: false,
    electricMirrors: false,
    cruiseControl: false,
    electricWindows: false,
    steeringWheelControls: false,
    powerSteering: false,
    fogLights: false,
    centralLock: false,
    airbags: false,
    tirePressureSensor: false
  });

  // Sale-specific details
  const [price, setPrice] = useState('');
  const [adPackage, setAdPackage] = useState('free');
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  // Rental-specific details
  const [dailyPrice, setDailyPrice] = useState('');
  const [insuranceType, setInsuranceType] = useState('full');
  const [minRentalDays, setMinRentalDays] = useState('1');

  const brands = [
    'BMW', 'Mercedes', 'Volkswagen', 'Peugeot',
    'Abarth', 'Acura', 'Alfa Romeo', 'Apollo', 'Ariel', 'Aston Martin', 'Audi',
    'BAIC', 'Bentley', 'Borgward', 'Brilliance', 'Bugatti', 'Buick', 'BYD',
    'Cadillac', 'Changan', 'Chery', 'Chevrolet', 'Chrysler', 'Citroen', 'Cupra',
    'Dacia', 'Daewoo', 'Daihatsu', 'Datsun', 'De Tomaso', 'Dodge', 'DS Automobiles',
    'Ferrari', 'Fiat', 'Fisker', 'Ford', 'Geely', 'Genesis', 'GMC', 'Great Wall', 'Haval',
    'Honda', 'Hongqi', 'Hummer', 'Hyundai', 'Infiniti', 'Isuzu', 'JAC', 'Jaguar', 'Jeep',
    'Jetour', 'Kia', 'Koenigsegg', 'Lada', 'Lamborghini', 'Lancia', 'Land Rover', 'Leapmotor',
    'Lexus', 'Lifan', 'Lincoln', 'Lucid', 'Mahindra', 'Maserati', 'Maybach', 'Mazda', 'McLaren',
    'MG', 'Mini', 'Mitsubishi', 'Nio', 'Nissan', 'Opel', 'Pagani', 'Polestar', 'Pontiac',
    'Porsche', 'Proton', 'Qoros', 'Ram', 'Ravon', 'Renault', 'Rezvani', 'Rimac', 'Roewe',
    'Rolls-Royce', 'Saab', 'Seat', 'Seres', 'Skoda', 'Smart', 'SsangYong', 'Subaru', 'Suzuki',
    'Tata', 'Tesla', 'Toyota', 'VinFast', 'Volvo', 'Wey', 'Wuling', 'Xpeng', 'Yugo', 'Zotye'
  ];
  
  const fuelTypes = ['Бензин', 'Дизел', 'Хибрид', 'Електричен', 'Плин'];
  const transmissionTypes = ['Автоматски', 'Мануелен'];
  const insuranceTypes = ['Full Kasko', 'Regular Kasko', 'Basic'];
  const colors = ['Црна', 'Бела', 'Сребрена', 'Сива', 'Црвена', 'Сиња', 'Зелена', 'Жолта', 'Портокалова', 'Кафеава'];
  const bodyTypes = ['Седан', 'Хечбек', 'Комби', 'SUV', 'Џип', 'Кабриолет', 'Купе', 'Миниван', 'Пикап', 'Спортски'];
  const driveTypes = ['Преден погон', 'Заден погон', '4x4', 'All Wheel Drive'];
  const cities = [
    'Скопје', 'Битола', 'Прилеп', 'Куманово', 'Тетово', 'Охрид', 'Велес', 'Штип', 'Кавадарци', 'Гостивар',
    'Струмица', 'Неготино', 'Кочани', 'Кичево', 'Ресен', 'Гевгелија', 'Струга', 'Делчево', 'Виница', 'Кратово',
    'Крива Паланка', 'Берово', 'Македонски Брод', 'Дебар', 'Демир Капија', 'Демир Хисар', 'Пробиштип', 'Свети Николе',
    'Валандово', 'Богданци', 'Пехчево', 'Радовиш', 'Теарце', 'Желино', 'Липково', 'Чаир', 'Аеродром', 'Гази Баба',
    'Карпош', 'Кисела Вода', 'Центар', 'Ѓорче Петров', 'Сарај', 'Студеничани', 'Арачиново', 'Сопиште', 'Зелениково',
    'Илинден', 'Петровец', 'Чучер-Сандево', 'Зрновци', 'Маврово и Ростуше', 'Ранковце', 'Лозово', 'Новци', 'Новаци',
    'Конче', 'Дојран', 'Василево', 'Босилово', 'Ново Село', 'Карачиново', 'Куклиш', 'Могила', 'Пласница', 'Центар Жупа'
  ];
  
  useEffect(() => {
    const savedSellerType = localStorage.getItem('sellerType');
    if (savedSellerType) {
      setSellerType(savedSellerType);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
  
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserData(userData);
          console.log("USER DATA:", userData);

          if (userData.userType) {
            setSellerType(userData.userType);
            localStorage.setItem('sellerType', userData.userType);
          
            if (userData.userType === 'dealer') {
              setActiveStep(1); // Skip directly to ad type selection for dealers
            }
          }
  
          // Auto-fill user details
          if (userData.userType) setSellerType(userData.userType);
          if (userData.dealerName) setCompanyName(userData.dealerName);
          if (userData.phone) setContactNumber(userData.phone);
          if (userData.city) setCity(userData.city);
          if (userData.avatar) setCompanyLogo(userData.avatar);
          if (userData.address) setCompanyAddress(userData.address);
          if (userData.facebookLink) setFacebookLink(userData.facebookLink);
          if (userData.instagramLink) setInstagramLink(userData.instagramLink);
          if (userData.description) setCompanyDescription(userData.description);
          if (userData.plan) setAdPackage(userData.plan);
          
          // Skip payment step if dealer has active plan
          if (userData.userType === 'dealer' && userData.activePlan) {
            setAdPackage(userData.activePlan);
          }

          // Get user's ad count
          const adsQuery = query(collection(db, "ads"), where("userId", "==", currentUser.uid));
          const querySnapshot = await getDocs(adsQuery);
          setUserAdCount(querySnapshot.size);
        }
      }
    });
  
    return () => unsubscribe();
  }, []);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 10) {
      setError('Максимално можете да прикачите 10 слики');
      return;
    }
    setImages([...images, ...files]);
  };

  const handleCompanyLogoUpload = (e) => {
    const file = e.target.files[0];
    setCompanyLogo(file);
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      setStepCompleted(true);
      return;
    }
    
    if (activeStep === 0) {
      if (!sellerType) {
        setError('Ве молам изберете тип на продавач');
        return;
      }
    }

    if (activeStep === 1 && sellerType === 'dealer') {
      if (!companyName || !companyAddress || !contactNumber || !city) {
        setError('Ве молам пополнете ги сите задолжителни полиња');
        return;
      }
    }
    
    // Skip plan selection step for dealers with active plan
    if (
      sellerType === 'dealer' &&
      userData?.activePlan &&
      steps[activeStep] === 'Пакет и град'
    ) {
      setActiveStep(activeStep + 1);
      return;
    }
    
    setError('');
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    // Skip back over payment step if dealer has active plan
    if (sellerType === 'dealer' && userData?.activePlan && activeStep === steps.length - 1) {
      setActiveStep(activeStep - 1);
    } else {
      setActiveStep(activeStep - 1);
    }
  };

  const handleEquipmentChange = (e) => {
    setEquipment({
      ...equipment,
      [e.target.name]: e.target.checked
    });
  };

  const uploadImages = async () => {
    const imageUrls = [];
    const uploadPromises = [];

    for (const image of images) {
      const filePath = `cars/${Date.now()}_${image.name}`;
      const fileRef = ref(storage, filePath);
      
      const uploadTask = uploadBytesResumable(fileRef, image);
      
      uploadPromises.push(
        new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              reject(error);
            },
            async () => {
              try {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                imageUrls.push(url);
                resolve();
              } catch (error) {
                reject(error);
              }
            }
          );
        })
      );
    }

    await Promise.all(uploadPromises);
    return imageUrls;
  };

  const uploadCompanyLogo = async () => {
    if (!companyLogo) return null;
    
    const filePath = `logos/${Date.now()}_${companyLogo.name}`;
    const fileRef = ref(storage, filePath);
    
    const uploadTask = uploadBytesResumable(fileRef, companyLogo);
    
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          reject(error);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    setUploadProgress(0);
    
    if (!user) {
      setShowLoginDialog(true);
      setLoading(false);
      return;
    }

    // Private sellers must always pay for premium/urgent packages
    if (sellerType === 'private' && ['premium', 'urgent'].includes(adPackage) && !paymentCompleted) {
      setError('Мора да го завршите плаќањето пред да го објавите огласот.');
      setLoading(false);
      return;
    }
    
    // Check if dealer has reached free ad limit and hasn't paid for a plan
    if (dealerLimitReached) {
      setError('Го достигнавте максималниот број на огласи за вашиот профил. Ве молиме изберете план за да продолжите.');
      setLoading(false);
      return;
    }

    // Ensure plan is selected for private users or dealers without active plan
    if ((sellerType === 'private' && !adPackage) || 
        (sellerType === 'dealer' && !userData?.activePlan && !adPackage)) {
      setError('Ве молам изберете пакет пред да објавите оглас');
      setLoading(false);
      return;
    }

    if (sellerType === 'dealer' && (!brand || !model || !year || !fuelType || !transmission || !enginePower || 
        (adType === 'sell' && !price) || (adType === 'rent' && !dailyPrice))) {
      setError('Ве молам пополнете ги сите задолжителни полиња');
      setLoading(false);
      return;
    }

    if (sellerType === 'private' && (!brand || !model || !year || !fuelType || !transmission || !enginePower || !price)) {
      setError('Ве молам пополнете ги сите задолжителни полиња');
      setLoading(false);
      return;
    }

    try {
      let imageUrls = [];
      let logoUrl = null;
      
      if (images.length > 0) {
        imageUrls = await uploadImages();
      }
      
      if (companyLogo && typeof companyLogo !== 'string') {
        logoUrl = await uploadCompanyLogo();
      } else if (typeof companyLogo === 'string') {
        logoUrl = companyLogo;
      }

      // Determine the package to use
      const finalPackage = sellerType === 'dealer' && userData?.activePlan ? userData.activePlan : adPackage;

      // Create ad document in Firestore
      const adData = {
        adType,
        carType,
        sellerType,
        brand,
        model,
        year: parseInt(year),
        fuelType,
        transmission,
        enginePower: parseInt(enginePower),
        mileage: mileage ? parseInt(mileage) : 0,
        registered,
        urgent,
        contactNumber,
        whatsappNumber,
        viberNumber,
        description,
        color,
        carBodyType,
        driveType,
        equipment,
        price: parseFloat(price),
        dailyPrice: dailyPrice ? parseFloat(dailyPrice) : null,
        insuranceType: adType === 'rent' ? insuranceType : null,
        minRentalDays: adType === 'rent' ? parseInt(minRentalDays) : null,
        images: imageUrls,
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || '',
        createdAt: serverTimestamp(),
        status: "active",
        isPremium: sellerType === 'private' && (
          finalPackage === 'premium' || finalPackage === 'basic' || finalPackage === 'recomended'
        ),
        isUrgent: sellerType === 'private' && (
          finalPackage === 'urgent' || finalPackage === 'basic' || finalPackage === 'recomended'
        ),
        
        city,
        package: finalPackage,
        expiresAt: sellerType === 'private' ? 
          (finalPackage === 'free' ? new Date(Date.now() + 45 * 24 * 60 * 60 * 1000) : new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)) :
          new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      };

      if (sellerType === 'dealer') {
        adData.companyName = companyName;
        adData.companyLogo = logoUrl;
        adData.companyAddress = companyAddress;
        adData.companyDescription = companyDescription;
        adData.facebookLink = facebookLink;
        adData.instagramLink = instagramLink;
        adData.dealerSlug = userData?.dealerSlug || '';
      }

      await addDoc(collection(db, "ads"), adData);

      if (sellerType === 'dealer') {
        await addDoc(collection(db, "inventory"), {
          ...adData,
          purchasePrice: null,
          reserved: false,
          addedAt: serverTimestamp(),
        });
      }
      
      setShowSuccess(true);
      setTimeout(() => {
        // Redirect to home page after successful ad posting
        navigate('/');
      }, 2000);      
    } catch (err) {
      console.error("Error submitting ad:", err);
      setError('Грешка при објавување на оглас. Обидете се повторно!');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  const handleCloseLoginDialog = () => {
    setShowLoginDialog(false);
  };

  const handleNavigateToLogin = () => {
    navigate('/login');
    setShowLoginDialog(false);
  };

  const handleNavigateToRegister = () => {
    navigate('/register');
    setShowLoginDialog(false);
  };

  const steps = sellerType === 'dealer' ? [
    'Информации за салон',
    'Тип на оглас',
    'Основни информации',
    'Технички детали',
    'Дополнителни информации',
    userData?.activePlan ? '' : 'Пакет и град'
  ].filter(step => step !== '') : [
    'Тип на оглас',
    'Основни информации',
    'Технички детали',
    'Дополнителни информации',
    'Пакет и град'
  ];

  const getStepContent = (step) => {
    // Adjust step index for dealer flow
    const adjustedStep = sellerType === 'dealer' ? step : step + 1;
    
    if (sellerType === 'dealer' && step === 0) {
      return (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Информации за вашиот авто салон/плац
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={companyLogo ? (typeof companyLogo === 'string' ? companyLogo : URL.createObjectURL(companyLogo)) : ''}
                sx={{ width: 120, height: 120, mb: 2 }}
              >
                {!companyLogo && <Business sx={{ fontSize: 60 }} />}
              </Avatar>
              <Button
                variant="contained"
                component="label"
                startIcon={<PhotoCamera />}
              >
                Додади лого
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleCompanyLogoUpload}
                />
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Име на салон/плац*"
              fullWidth
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Адреса*"
              fullWidth
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Телефон*"
              fullWidth
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="WhatsApp број"
              fullWidth
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Viber број"
              fullWidth
              value={viberNumber}
              onChange={(e) => setViberNumber(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Град*</InputLabel>
              <StyledSelect
                value={city}
                label="Град*"
                onChange={(e) => setCity(e.target.value)}
                required
              >
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              label="Опис на вашиот салон/плац"
              multiline
              rows={3}
              fullWidth
              value={companyDescription}
              onChange={(e) => setCompanyDescription(e.target.value)}
              placeholder="Опишете го вашиот бизнис (колку години работите, дали нудите финансирање/лизинг, итн.)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Facebook линк"
              fullWidth
              value={facebookLink}
              onChange={(e) => setFacebookLink(e.target.value)}
              placeholder="https://facebook.com/вашастраница"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Instagram линк"
              fullWidth
              value={instagramLink}
              onChange={(e) => setInstagramLink(e.target.value)}
              placeholder="https://instagram.com/вашастраница"
            />
          </Grid>
        </Grid>
      );
    }

    if ((sellerType === 'dealer' && step === 1) || (sellerType === 'private' && step === 0)) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
            Што сакате да направите?
          </Typography>
          <RadioGroup
            value={adType}
            onChange={(e) => setAdType(e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel 
              value="sell" 
              control={<Radio color="primary" />} 
              label="Сакам да продадам автомобил" 
              sx={{ mb: 1 }}
            />
            {sellerType === 'dealer' && (
              <FormControlLabel 
                value="rent" 
                control={<Radio color="primary" />} 
                label="Сакам да изнајмам автомобил" 
              />
            )}
          </RadioGroup>

          {adType === 'sell' && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600, color: 'text.primary' }}>
                Тип на автомобил
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  label="Нов автомобил"
                  onClick={() => setCarType('new')}
                  color={carType === 'new' ? 'primary' : 'default'}
                  variant={carType === 'new' ? 'filled' : 'outlined'}
                  sx={{ p: 2, fontSize: '1rem', borderRadius: '8px' }}
                />
                <Chip
                  label="Користен автомобил"
                  onClick={() => setCarType('used')}
                  color={carType === 'used' ? 'primary' : 'default'}
                  variant={carType === 'used' ? 'filled' : 'outlined'}
                  sx={{ p: 2, fontSize: '1rem', borderRadius: '8px' }}
                />
              </Box>
            </>
          )}
        </Box>
      );
    }

    if ((sellerType === 'dealer' && step === 2) || (sellerType === 'private' && step === 1)) {
      return (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Бренд</InputLabel>
              <StyledSelect
                value={brand}
                label="Бренд"
                onChange={(e) => setBrand(e.target.value)}
                required
              >
                {brands.map((brand) => (
                  <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Модел"
              fullWidth
              value={model}
              onChange={(e) => setModel(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Година на производство"
              type="number"
              fullWidth
              value={year}
              onChange={(e) => setYear(e.target.value)}
              inputProps={{ min: 1990, max: new Date().getFullYear() + 1 }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Боја</InputLabel>
              <StyledSelect
                value={color}
                label="Боја"
                onChange={(e) => setColor(e.target.value)}
              >
                {colors.map((color) => (
                  <MenuItem key={color} value={color}>{color}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Тип на каросерија</InputLabel>
              <StyledSelect
                value={carBodyType}
                label="Тип на каросерија"
                onChange={(e) => setCarBodyType(e.target.value)}
              >
                {bodyTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Тип на погон</InputLabel>
              <StyledSelect
                value={driveType}
                label="Тип на погон"
                onChange={(e) => setDriveType(e.target.value)}
              >
                {driveTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>
          {adType === 'sell' ? (
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label={carType === 'new' ? "Цена (€)" : "Цена (€)"}
                type="number"
                fullWidth
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  label="Цена по ден (€)"
                  type="number"
                  fullWidth
                  value={dailyPrice}
                  onChange={(e) => setDailyPrice(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Тип на осигурување</InputLabel>
                  <StyledSelect
                    value={insuranceType}
                    label="Тип на осигурување"
                    onChange={(e) => setInsuranceType(e.target.value)}
                  >
                    {insuranceTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  label="Минимален број на денови за изнајмување"
                  type="number"
                  fullWidth
                  value={minRentalDays}
                  onChange={(e) => setMinRentalDays(e.target.value)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </>
          )}
        </Grid>
      );
    }

    if ((sellerType === 'dealer' && step === 3) || (sellerType === 'private' && step === 2)) {
      return (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Тип на гориво</InputLabel>
              <StyledSelect
                value={fuelType}
                label="Тип на гориво"
                onChange={(e) => setFuelType(e.target.value)}
                required
              >
                {fuelTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Тип на менувач</InputLabel>
              <StyledSelect
                value={transmission}
                label="Тип на менувач"
                onChange={(e) => setTransmission(e.target.value)}
                required
              >
                {transmissionTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Моќност на мотор (КС)"
              type="number"
              fullWidth
              value={enginePower}
              onChange={(e) => setEnginePower(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              label="Километража (km)"
              type="number"
              fullWidth
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Регистриран?</InputLabel>
              <StyledSelect
                value={registered}
                label="Регистриран?"
                onChange={(e) => setRegistered(e.target.value)}
                required
              >
                <MenuItem value="yes">Да</MenuItem>
                <MenuItem value="no">Не</MenuItem>
              </StyledSelect>
            </FormControl>
          </Grid>
          
          {/* Equipment Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
              Опрема
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {Object.entries(equipment).map(([key, value]) => (
                <Grid item xs={6} sm={4} md={3} key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={handleEquipmentChange}
                        name={key}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {key === 'esp' && 'ESP'}
                        {key === 'abs' && 'ABS'}
                        {key === 'navigation' && 'Навигација'}
                        {key === 'electricSeats' && 'Елек. седишта'}
                        {key === 'climate' && 'Клима'}
                        {key === 'electricMirrors' && 'Елек. ретровизори'}
                        {key === 'cruiseControl' && 'Темпомат'}
                        {key === 'electricWindows' && 'Елек. прозорци'}
                        {key === 'steeringWheelControls' && 'Команди на волан'}
                        {key === 'powerSteering' && 'Серво волан'}
                        {key === 'fogLights' && 'Магленки'}
                        {key === 'centralLock' && 'Централно заклучување'}
                        {key === 'airbags' && 'Airbags'}
                        {key === 'tirePressureSensor' && 'Сензор за гуми'}
                      </Typography>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      );
    }

    if ((sellerType === 'dealer' && step === 4) || (sellerType === 'private' && step === 3)) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Опис на автомобилот
          </Typography>
          <StyledTextField
            label="Детали за автомобилот"
            multiline
            rows={4}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Опишете го вашиот автомобил во детали..."
          />

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Слики од автомобилот
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Додадете најмалку 3 слики за подобри резултати (макс. 10)
            </Typography>
            <StyledButton
              variant="contained"
              component="label"
              startIcon={<PhotoCamera />}
              sx={{ mb: 2 }}
            >
              Додади слики
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleImageUpload}
              />
            </StyledButton>
            <Grid container spacing={2}>
              {images.map((image, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <Box
                      component="img"
                      src={URL.createObjectURL(image)}
                      alt={`Car ${index}`}
                      sx={{ 
                        width: '100%', 
                        height: '120px', 
                        objectFit: 'cover', 
                        borderRadius: '12px',
                        border: '1px solid #eee',
                        boxShadow: 1
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8, 
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)'
                        }
                      }}
                      onClick={() => handleRemoveImage(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {sellerType === 'private' && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Контакт информации
              </Typography>
              <StyledTextField
                label="Контакт телефон*"
                fullWidth
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <StyledTextField
                label="WhatsApp број (опционално)"
                fullWidth
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                sx={{ mb: 2 }}
              />
              <StyledTextField
                label="Viber број (опционално)"
                fullWidth
                value={viberNumber}
                onChange={(e) => setViberNumber(e.target.value)}
              />
            </Box>
          )}
        </Box>
      );
    }

    if ((sellerType === 'dealer' && step === 5 && !userData?.activePlan) || (sellerType === 'private' && step === 4)) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Изберете пакет за вашиот оглас
          </Typography>
          
          {sellerType === 'private' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <PlanCard 
                  selected={adPackage === 'free'}
                  onClick={() => setAdPackage('free')}
                  color="#1976d2"
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ 
                        bgcolor: adPackage === 'free' ? '#1976d2' : '#eee', 
                        mr: 2,
                        color: adPackage === 'free' ? '#fff' : 'text.secondary'
                      }}>
                        <CheckCircle />
                      </Avatar>
                      <Typography variant="h6">Бесплатен</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Основен оглас со сите функции
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 1 }}>0 ден.</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Траење: 45 дена
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        • Автоматски бришење по 45 дена
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {adPackage === 'free' && (
                        <Chip label="Избран" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </PlanCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <PlanCard 
                  selected={adPackage === 'premium'}
                  onClick={() => setAdPackage('premium')}
                  color="#4caf50"
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ 
                        bgcolor: adPackage === 'premium' ? '#4caf50' : '#eee', 
                        mr: 2,
                        color: adPackage === 'premium' ? '#fff' : 'text.secondary'
                      }}>
                        <Star />
                      </Avatar>
                      <Typography variant="h6">Премиум</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Препорачан за подобри резултати
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 1 }}>150 ден.</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Траење: 60 дена
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        • Огласот е означен како Премиум
                      </Typography>
                      <Typography variant="body2">
                        • Прикажан на почетна страница
                      </Typography>
                      <Typography variant="body2">
                        • Подолг животен век на огласот
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {adPackage === 'premium' && (
                        <Chip label="Избран" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </PlanCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <PlanCard 
                  selected={adPackage === 'urgent'}
                  onClick={() => setAdPackage('urgent')}
                  color="#ff9800"
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ 
                        bgcolor: adPackage === 'urgent' ? '#ff9800' : '#eee', 
                        mr: 2,
                        color: adPackage === 'urgent' ? '#fff' : 'text.secondary'
                      }}>
                        <LocalOffer />
                      </Avatar>
                      <Typography variant="h6">Итен</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      За брза продажба
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 1 }}>200 ден.</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Траење: додека не се продаде
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        • Огласот е означен како "Итно"
                      </Typography>
                      <Typography variant="body2">
                        • Прикажан на врвот на резултатите
                      </Typography>
                      <Typography variant="body2">
                        • Црвена ознака за внимание
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {adPackage === 'urgent' && (
                        <Chip label="Избран" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </PlanCard>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <PlanCard 
                  selected={adPackage === 'free'}
                  onClick={() => setAdPackage('free')}
                  color="#1976d2"
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ 
                        bgcolor: adPackage === 'free' ? '#1976d2' : '#eee', 
                        mr: 2,
                        color: adPackage === 'free' ? '#fff' : 'text.secondary'
                      }}>
                        <CheckCircle />
                      </Avatar>
                      <Typography variant="h6">Бесплатен</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Добар за почеток
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 1 }}>0 ден. годишно</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        • 10 огласи
                      </Typography>
                      <Typography variant="body2">
                        • Професионален dashboard
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {adPackage === 'free' && (
                        <Chip label="Избран" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </PlanCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <PlanCard 
                  selected={adPackage === 'basic'}
                  onClick={() => setAdPackage('basic')}
                  color="#ff9800"
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ 
                        bgcolor: adPackage === 'basic' ? '#ff9800' : '#eee', 
                        mr: 2,
                        color: adPackage === 'basic' ? '#fff' : 'text.secondary'
                      }}>
                        <LocalOffer />
                      </Avatar>
                      <Typography variant="h6">Стандарден</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Идеален за мали салони и плацеви
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 1 }}>500 ден. годишно</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        • 100 огласи
                      </Typography>
                      <Typography variant="body2">
                        • 1 премиум оглас
                      </Typography>
                      <Typography variant="body2">
                        • 1 итен оглас
                      </Typography>
                      <Typography variant="body2">
                        • Професионален dashboard
                      </Typography>
                      <Typography variant="body2">
                        • Лого и Инфо во огласите
                      </Typography>
                      <Typography variant="body2">
                        • Посебна страна за вашиот Салон или Плац
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {adPackage === 'basic' && (
                        <Chip label="Избран" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </PlanCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <PlanCard 
                  selected={adPackage === 'recomended'}
                  onClick={() => setAdPackage('recomended')}
                  color="#4caf50"
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ 
                        bgcolor: adPackage === 'recomended' ? '#4caf50' : '#eee', 
                        mr: 2,
                        color: adPackage === 'recomended' ? '#fff' : 'text.secondary'
                      }}>
                        <Star />
                      </Avatar>
                      <Typography variant="h6">Премиум</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Најдобар избор за професионалци
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 1 }}>1,000 ден. годишно</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        • Неограничено објавување огласи
                      </Typography>
                      <Typography variant="body2">
                        • 5 премиум огласи
                      </Typography>
                      <Typography variant="body2">
                        • 3 итен огласи
                      </Typography>
                      <Typography variant="body2">
                        • Професионален dashboard
                      </Typography>
                      <Typography variant="body2">
                        • Прилагодена URL адреса (avtoklik.com/вашиотсалон)
                      </Typography>
                      <Typography variant="body2">
                        • Верифициран Знак за доверба
                      </Typography>
                      <Typography variant="body2">
                        • Маркетинг низ АвтоКлик медиумите и реклама на веб страната
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {adPackage === 'recomended' && (
                        <Chip label="Избран" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </PlanCard>
              </Grid>
            </Grid>
          )}

          {adPackage !== 'free' && (
            <Box
              sx={{
                mt: 4,
                p: 3,
                bgcolor: 'background.default',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Завршете го плаќањето
              </Typography>

              <Box sx={{ mt: 2 }}>
                <StripePayment
                  amountMKD={
                    adPackage === 'premium' ? 150 :
                    adPackage === 'urgent' ? 200 :
                    adPackage === 'basic' ? 500 :
                    adPackage === 'recomended' ? 1000 : 0
                  }
                  onPaymentSuccess={async () => {
                    setPaymentCompleted(true);
                    setError('');
                  
                    // Update premiumCredits in Firestore if dealer and plan is basic or recomended
                    if (user && sellerType === 'dealer') {
                      const userRef = doc(db, 'users', user.uid);
                      let updateData = {};
                  
                      if (adPackage === 'basic') {
                        updateData = {
                          activePlan: 'basic',
                          premiumCredits: 1,
                          urgentCredits: 1
                        };
                      } else if (adPackage === 'recomended') {
                        updateData = {
                          activePlan: 'premium',
                          premiumCredits: 5,
                          urgentCredits: 3
                        };
                      }
                  
                      if (Object.keys(updateData).length > 0) {
                        await updateDoc(userRef, updateData);
                      }
                    }
                  }}
                  onPaymentError={(err) => {
                    console.error(err);
                    setError("Неуспешно плаќање. Обидете се повторно.");
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return 'Unknown step';
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <StyledPaper>
        <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: '700', mb: 4, color: 'primary.main' }}>
          Објави нов оглас
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StyledStepLabel>{label}</StyledStepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={(e) => {
          e.preventDefault();
          if (stepCompleted) {
            handleSubmit(e);
          } else {
            handleNext();
          }
        }}>
  
          {getStepContent(activeStep)}

          {loading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress 
                variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'} 
                value={uploadProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
              {uploadProgress > 0 && (
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  Се прикачуваат слики... {uploadProgress}%
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <StyledButton
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              sx={{ 
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px'
                }
              }}
            >
              Назад
            </StyledButton>
            {activeStep < steps.length - 1 ? (
              <StyledButton
                variant="contained"
                onClick={handleNext}
                sx={{
                  background: 'linear-gradient(to right, #1976d2, #2196f3)',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
              >
                Продолжи
              </StyledButton>
            ) : (
              <StyledButton
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading}
                sx={{ 
                  minWidth: '150px',
                  background: 'linear-gradient(to right, #4caf50, #66bb6a)',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
              >
                {loading ? 'Се обработува...' : 'Објави оглас'}
              </StyledButton>
            )}
          </Box>
        </form>
      </StyledPaper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccess} 
          severity="success" 
          sx={{ width: '100%' }}
          variant="filled"
        >
          Огласот е успешно објавен!
        </Alert>
      </Snackbar>

      <Dialog 
        open={showLoginDialog} 
        onClose={handleCloseLoginDialog}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '16px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Најава потребна</DialogTitle>
        <DialogContent>
          <Typography>Мора да сте најавени за да можете да објавите оглас.</Typography>
        </DialogContent>
        <DialogActions>
          <StyledButton 
            onClick={handleCloseLoginDialog}
            variant="outlined"
            sx={{ borderWidth: '2px', '&:hover': { borderWidth: '2px' } }}
          >
            Откажи
          </StyledButton>
          <StyledButton 
            onClick={handleNavigateToRegister} 
            color="primary"
            variant="outlined"
            sx={{ borderWidth: '2px', '&:hover': { borderWidth: '2px' } }}
          >
            Регистрирај се
          </StyledButton>
          <StyledButton 
            onClick={handleNavigateToLogin} 
            color="primary" 
            variant="contained"
            sx={{
              background: 'linear-gradient(to right, #1976d2, #2196f3)',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            Најави се
          </StyledButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PostAd;